import PropTypes from 'prop-types';
import { Box, Button, Slide, Stack, Typography } from '@mui/material';
import { CheckCircleOutlined, HighlightOffOutlined, ErrorOutlineOutlined, ClearOutlined } from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { ATTENDANCE_STATUS_META } from '../utils/session-labels';

const styles = {
  bar: {
    position: 'fixed',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    bgcolor: colors.BLACK,
    color: '#fff',
    px: 3,
    py: 1.5,
    borderRadius: 999,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    zIndex: theme => theme.zIndex.modal + 1,
  },
  actionBtn: meta => ({
    color: '#fff',
    bgcolor: meta.color,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 12.5,
    px: 1.5,
    '&:hover': { bgcolor: meta.color, opacity: 0.92 },
  }),
};

/**
 * Sticky bulk-action bar shown when ≥1 attendance row is selected.
 * The bar is "dumb": it just emits the chosen status; the parent decides how to
 * compose the bulk payload and call `bulkSave`.
 */
export default function AttendanceBulkBar({ count, onMark, onClear, disabled }) {
  const { t2 } = useTranslation('EditCourseSessions');

  if (count === 0) return null;

  return (
    <Slide in direction="up" mountOnEnter unmountOnExit>
      <Box sx={styles.bar}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t2('attendance.bulkBar', [count])}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<CheckCircleOutlined sx={{ fontSize: 16 }} />}
            onClick={() => onMark('present')}
            disabled={disabled}
            sx={styles.actionBtn(ATTENDANCE_STATUS_META.present)}
            disableElevation
          >
            {t2('attendance.bulkMarkPresent')}
          </Button>
          <Button
            size="small"
            startIcon={<HighlightOffOutlined sx={{ fontSize: 16 }} />}
            onClick={() => onMark('absent')}
            disabled={disabled}
            sx={styles.actionBtn(ATTENDANCE_STATUS_META.absent)}
            disableElevation
          >
            {t2('attendance.bulkMarkAbsent')}
          </Button>
          <Button
            size="small"
            startIcon={<ErrorOutlineOutlined sx={{ fontSize: 16 }} />}
            onClick={() => onMark('justified')}
            disabled={disabled}
            sx={styles.actionBtn(ATTENDANCE_STATUS_META.justified)}
            disableElevation
          >
            {t2('attendance.bulkMarkJustified')}
          </Button>
        </Stack>
        <Button
          size="small"
          startIcon={<ClearOutlined sx={{ fontSize: 16 }} />}
          onClick={onClear}
          sx={{ color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: 12.5 }}
        >
          {t2('form.cancel')}
        </Button>
      </Box>
    </Slide>
  );
}

AttendanceBulkBar.propTypes = {
  count: PropTypes.number.isRequired,
  onMark: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

AttendanceBulkBar.defaultProps = { disabled: false };
